import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { SearchLawAndLawCommentaryParams } from '~/utils/solr/params'
import { SolrParamsKey } from '#shared/solr'
import { collapseLawFragmentChildId, sortByLawFragmentId } from '~/utils/solr/law-utils'

type DocWithHighlights = LawAndCommentary & { highlights: { field: string, text: string }[] }

export function useLawAndCommentarySearch(searchTerm: Ref<string>, additionalParams?: MaybeRef<Partial<SearchLawAndLawCommentaryParams>>) {
  const queryParams = computed(() => ({
    [SolrParamsKey.SearchLawAndLawCommentary]: {
      searchTerm: searchTerm.value,
      lawId: toValue(additionalParams)?.lawId,
      commentId: toValue(additionalParams)?.commentId,
    },
  }))

  const { data: searchData, isLoading } = useLawCommentaryQuery('search', queryParams)

  const allMatchedDocs = computed<DocWithHighlights[]>(() => {
    const result = searchData?.value
    if (!result?.response) {
      return []
    }
    const highlighting = result.highlighting
    return result.response.docs.map((doc) => {
      const h = highlighting?.[doc.id]
      const highlights = [
        ...(h?.bodytext || []).map(text => ({ field: 'bodytext', text })),
        ...(h?.title || []).map(text => ({ field: 'title', text })),
        ...(h?.paragraphTitle?.length ? [{ field: 'title', text: `<span class="highlight">${doc.paragraphTitle}</span>` }] : []),
        ...(h?.chapterTitle?.length && doc.chapterTitle ? [{ field: 'title', text: `<span class="highlight">${doc.chapterTitle}</span>` }] : []),
      ]
        .filter(hl => hl.text && hl.text.trim().length > 0)
      return { ...doc, highlights }
    })
  })

  const searchResult = computed(() => {
    const result = searchData?.value ?? null
    if (!result || !result.response) {
      return null
    }

    const uniqueDocs = new Map<string, DocWithHighlights>()
    for (const doc of allMatchedDocs.value) {
      // Deduplicate by fragment/node ID when available. Child paragraph results are grouped, while highlighting still uses all matches
      const rawId = String(doc.lawFragmentId || doc.ezMainNodeId || doc.id)
      const id = collapseLawFragmentChildId(rawId)
      if (!uniqueDocs.has(id)) {
        uniqueDocs.set(id, doc)
      }
    }

    return {
      ...result,
      response: {
        ...result.response,
        docs: sortByLawFragmentId(Array.from(uniqueDocs.values())),
        numFound: uniqueDocs.size,
      },
    }
  })

  return {
    isLoading,
    searchResult,
    allMatchedDocs,
  }
}
